import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import superAdminApiClient from '../../../api/superAdminApiClient';
import Pagination from '../../../components/common/Pagination';
import ContentLoader from '../../../components/common/ContentLoader';
import SEO from '../../../components/common/SEO';
import { formatDate } from '../../../utils/dateUtils';

const TrashVendorListing = () => {
    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(false);

    // Filters & Pagination
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState('all'); // 'all', 'today', '7days', '30days', 'expiring'
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);

    const fetchTrashVendors = useCallback(async () => {
        setLoading(true);
        try {
            const queryParams = new URLSearchParams({
                page,
                limit: 20,
                ...(search ? { search } : {}),
                ...(filter !== 'all' ? { filter } : {})
            });
            const response = await superAdminApiClient.get(`/superadmin/trash/vendors?${queryParams}`);
            if (response.data && response.data.success) {
                setVendors(response.data.vendors);
                setTotalPages(response.data.total_pages);
                setTotalRecords(response.data.total_records);
            }
        } catch (error) {
            console.error('Error fetching trash vendors:', error);
        } finally {
            setLoading(false);
        }
    }, [page, search, filter]);

    useEffect(() => {
        fetchTrashVendors();
    }, [fetchTrashVendors]);

    const handleSearchChange = (e) => {
        setSearch(e.target.value);
        setPage(1);
    };

    const handleFilterChange = (newFilter) => {
        setFilter(newFilter);
        setPage(1);
    };

    return (
        <div className="page-contentss">
            <SEO title="Trash Vendors | Mangoloops SuperAdmin" description="Super Admin Trash Vendors Listing" />

            <div className="container-fluidss">
                {/* Page Header */}
                <div className="row">
                    <div className="col-12">
                        <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                            <h4 className="mb-sm-0 text-danger">
                                <i className="ri-delete-bin-line me-2"></i>Trash Vendors
                            </h4>
                            <div className="page-title-right">
                                <ol className="breadcrumb m-0">
                                    <li className="breadcrumb-item"><Link to="/superadmin/dashboard">SuperAdmin</Link></li>
                                    <li className="breadcrumb-item active">Trash Vendors</li>
                                </ol>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filter and Search Bar */}
                <div className="row mb-3">
                    <div className="col-md-6 mb-2 mb-md-0">
                        <div className="input-group">
                            <span className="input-group-text bg-light">
                                <i className="ri-search-line"></i>
                            </span>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search by Vendor Name, Company, Email, or Phone..."
                                value={search}
                                onChange={handleSearchChange}
                            />
                            {search && (
                                <button className="btn btn-outline-secondary" type="button" onClick={() => setSearch('')}>
                                    <i className="ri-close-line"></i>
                                </button>
                            )}
                        </div>
                    </div>
                    <div className="col-md-6 text-md-end">
                        <div className="btn-group" role="group" aria-label="Trash Filter Options">
                            <button
                                type="button"
                                className={`btn btn-sm ${filter === 'all' ? 'btn-danger' : 'btn-outline-danger'}`}
                                onClick={() => handleFilterChange('all')}
                            >
                                All Trash
                            </button>
                            <button
                                type="button"
                                className={`btn btn-sm ${filter === 'today' ? 'btn-danger' : 'btn-outline-danger'}`}
                                onClick={() => handleFilterChange('today')}
                            >
                                Deleted Today
                            </button>
                            <button
                                type="button"
                                className={`btn btn-sm ${filter === '7days' ? 'btn-danger' : 'btn-outline-danger'}`}
                                onClick={() => handleFilterChange('7days')}
                            >
                                Last 7 Days
                            </button>
                            <button
                                type="button"
                                className={`btn btn-sm ${filter === '30days' ? 'btn-danger' : 'btn-outline-danger'}`}
                                onClick={() => handleFilterChange('30days')}
                            >
                                Last 30 Days
                            </button>
                            <button
                                type="button"
                                className={`btn btn-sm ${filter === 'expiring' ? 'btn-warning text-dark fw-bold' : 'btn-outline-warning text-dark'}`}
                                onClick={() => handleFilterChange('expiring')}
                            >
                                <i className="ri-alarm-warning-line me-1"></i>Expiring Soon
                            </button>
                        </div>
                    </div>
                </div>

                {/* Vendors Table */}
                <div className="row">
                    <div className="col-12">
                        <div className="card">
                            <div className="card-body">
                                {loading ? (
                                    <ContentLoader />
                                ) : (
                                    <div className="table-responsive">
                                        <table className="table align-middle table-nowrap mb-0">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>SR No.</th>
                                                    <th>Vendor Name</th>
                                                    <th>Company Name</th>
                                                    <th>Email</th>
                                                    <th>Phone</th>
                                                    <th>Deleted Date</th>
                                                    <th>Days Remaining</th>
                                                    <th>Total Listings</th>
                                                    <th>Status</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {vendors.length > 0 ? (
                                                    vendors.map((v, index) => (
                                                        <tr key={v.id}>
                                                            <td>{(page - 1) * 20 + index + 1}</td>
                                                            <td>
                                                                <Link to={`/superadmin/trash/vendors/${v.id}`} className="fw-semibold text-danger">
                                                                    <i className="ri-user-line me-1"></i>{v.name || '-'}
                                                                </Link>
                                                            </td>
                                                            <td>{v.company_name ? v.company_name : '-'}</td>
                                                            <td>{v.email ? v.email : '-'}</td>
                                                            <td>{v.phone}</td>
                                                            <td>{v.deleted_at ? formatDate(v.deleted_at, 'DD-MM-YYYY HH:mm') : '-'}</td>
                                                            <td>
                                                                {v.days_remaining <= 7 ? (
                                                                    <span className="badge bg-warning text-dark px-2 py-1 fs-12">
                                                                        <i className="ri-error-warning-line me-1"></i>{v.days_remaining} Days Left
                                                                    </span>
                                                                ) : (
                                                                    <span className="badge bg-soft-danger text-danger px-2 py-1 fs-12">
                                                                        {v.days_remaining} Days Left
                                                                    </span>
                                                                )}
                                                            </td>
                                                            <td>
                                                                <span className="badge bg-secondary rounded-pill px-3 py-1">
                                                                    {v.total_listings}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <span className="badge bg-danger">In Trash</span>
                                                            </td>
                                                            <td>
                                                                <Link
                                                                    to={`/superadmin/trash/vendors/${v.id}`}
                                                                    className="btn btn-sm btn-outline-danger"
                                                                >
                                                                    <i className="ri-eye-line me-1"></i>View Details
                                                                </Link>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="10" className="text-center py-4 text-muted">
                                                            <i className="ri-delete-bin-5-line fs-32 d-block mb-2"></i>
                                                            No trash vendors found matching criteria.
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>

                                        <div className="d-flex justify-content-between align-items-center mt-3">
                                            <div>
                                                Showing {vendors.length} of {totalRecords} trash vendors
                                            </div>
                                            <Pagination
                                                currentPage={page}
                                                totalPages={totalPages}
                                                onPageChange={(p) => setPage(p)}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default TrashVendorListing;
